import { Component, Prop, Vue } from 'vue-property-decorator';
import { VigenciaSni } from '@conacyt/historicopersonasms-client';
import * as dateUtils from '@/shared/date/date-utils';
import * as formatterUtils from '@/shared/formatter/formatter-utils';

import HistoryItemComponent from '@/components/expediente-persona/history-item/history-item.vue';

@Component({
  components: {
    'history-item': HistoryItemComponent,
  },
})
export default class VigenciasSniComponent extends Vue {
  @Prop({ required: false })
  public item: Array<VigenciaSni>;

  private readonly MOD_VIGENCIA_BY_CLAVE: Record<string, string> = {
    '1': 'Maternidad o Paternidad',
    '3': '65 años de edad y 15 años en el SNI',
    '12': 'Extensión por integrante de comisión',
    '43': 'Enfermedad grave',
    '44': 'Situación familiar grave',
    '62': 'Extensión por reglamento',
    '83': 'Terminación anticipada',
    '23': 'Finalizó cargo administrativo o de elección popular',
    '52': 'Incapacidad permanente',
  };

  private get CLAVES_EXTENSION(): Set<string> {
    return new Set(Object.keys(this.MOD_VIGENCIA_BY_CLAVE));
  }

  /**
   * Construye las filas a mostrar en el grid:
   * - Las extensiones no se muestran directamente.
   * - Cada extensión se presenta como una fila “vista” asociada a su vigencia base,
   *   agregando la etiqueta de modificación correspondiente.
   */
  private buildDisplayRows(): Array<VigenciaSni> {
    const vigencias = this.item || [];
    if (!vigencias.length) return [];

    const getClaveTipo = (v: VigenciaSni) => String((v as any)?.tipo?.clave ?? '');
    const isExtension = (v: VigenciaSni) => this.CLAVES_EXTENSION.has(getClaveTipo(v));
    const getFI = (v: VigenciaSni) => (v as any)?.vigencia?.fechaInicio;
    const getFF = (v: VigenciaSni) => (v as any)?.vigencia?.fechaFin;

    const extensiones = vigencias.filter(isExtension);
    const normales = vigencias.filter(v => !isExtension(v));

    if (!extensiones.length) {
      return normales.map(v => ({ ...(v as any), __modificacionLabel: v.modificacionVigencia?.descripcion ?? '' } as any));
    }

    const matchBaseForExt = (ext: VigenciaSni): VigenciaSni | undefined => {
      const fiExt = getFI(ext);
      const ffExt = getFF(ext);

      if (fiExt) {
        const exact = normales.find(b => getFI(b) === fiExt);
        if (exact) return exact;
      }

      if (fiExt && ffExt) {
        const tFiExt = new Date(fiExt).getTime();
        const tFfExt = new Date(ffExt).getTime();

        const overlap = normales.find(b => {
          const fiB = getFI(b);
          const ffB = getFF(b);
          if (!fiB || !ffB) return false;

          const tFiB = new Date(fiB).getTime();
          const tFfB = new Date(ffB).getTime();

          return tFiB <= tFfExt && tFiExt <= tFfB;
        });
        if (overlap) return overlap;
      }

      if (fiExt) {
        const tExt = new Date(fiExt).getTime();
        const candidates = normales
          .map(b => ({ b, t: new Date(getFI(b)).getTime() }))
          .filter(x => !Number.isNaN(x.t) && x.t <= tExt)
          .sort((a, b) => b.t - a.t);

        return candidates[0]?.b;
      }

      return normales[0];
    };

    const extViewRows: Array<VigenciaSni> = extensiones.map(ext => {
      const base = matchBaseForExt(ext);

      const claveExt = getClaveTipo(ext);
      const modLabel = this.MOD_VIGENCIA_BY_CLAVE[claveExt];

      return {
        ...(ext as any),

        tipo: (base as any)?.tipo ?? (ext as any)?.tipo,

        __modificacionLabel: modLabel,
      } as any;
    });
    const normalesConCampo = normales.map(v => ({ ...(v as any), __modificacionLabel: v.modificacionVigencia?.descripcion ?? '' } as any));
    return [...extViewRows, ...normalesConCampo];
  }

  get vigenciasSni(): Array<VigenciaSni> {
    const rows = this.buildDisplayRows();

    return rows.sort((a, b) => {
      const activoCompare = Number((b as any).activo) - Number((a as any).activo);
      if (activoCompare !== 0) return activoCompare;

      const fechaInicioA = new Date((a as any).vigencia.fechaInicio).getTime();
      const fechaInicioB = new Date((b as any).vigencia.fechaInicio).getTime();
      const fechaInicioCompare = fechaInicioB - fechaInicioA;
      if (fechaInicioCompare !== 0) return fechaInicioCompare;

      const fechaFinA = new Date((a as any).vigencia.fechaFin).getTime();
      const fechaFinB = new Date((b as any).vigencia.fechaFin).getTime();
      return fechaFinB - fechaFinA;
    });
  }

  // Columna "Modificación de vigencia"
  //private modificacionVigenciaResolver(row: VigenciaSni): string {
  //return String((row as any).__modificacionLabel ?? '');
  //}

  get fields() {
    return [
      { key: 'activo', label: 'Vigencia activo', sortable: true },
      { key: 'tipo.descripcion', label: 'Tipo de vigencia', sortable: true },
      {
        key: '__modificacionLabel',
        label: 'Modificación de vigencia',
        sortable: true,
      },

      {
        key: 'mantieneApoyoEconomico',
        label: 'Cargo Admvo Activo',
        class: 'text-center',
        sortable: true,
        formatter: boolean => {
          return this.formatoBoleano(boolean);
        },
      },
      { key: 'nivelSni.descripcion', label: 'Nivel SNI', sortable: true },
      {
        key: 'vigencia.fechaInicio',
        label: 'Fecha inicio',
        sortable: true,
        formatter: value => dateUtils.formatUtc(value),
      },
      {
        key: 'vigencia.fechaFin',
        label: 'Fecha fin',
        sortable: true,
        formatter: value => dateUtils.formatUtc(value),
      },
      {
        key: 'urlConvenio',
        label: 'URL convenio',
        sortable: true,
        link: true,
        formatter: (value, key, item) => formatterUtils.vigenciaConvenioUrlResolver(value, key, item),
      },
      {
        key: 'urlReconocimiento',
        label: 'URL reconocimiento',
        sortable: true,
        formatter: (value, key, item) => formatterUtils.vigenciaReconocimientoUrlResolver(value, key, item),
      },
    ];
  }

  /**
   * Da formato a los boleanos
   * @param boolean
   * @returns
   */
  public formatoBoleano(boolean): string {
    if (boolean != null) {
      if (boolean == true) {
        return 'Sí';
      }
      return 'No';
    }
    return '';
  }
}
